'use client';

import { useEffect, useState } from 'react';
import { kstYmd } from '@/lib/member';

type PriceTrendRow = {
  id: string;
  date: string;
  broker: string;
  light: string;
  medium: string;
};

// PricingSection 차트/표와 같은 색상
const COLORS = {
  broker: '#ef4444',
  light: '#10b981',
  medium: '#3b82f6',
};

// "2026.01.30" -> "2026년 1월 30일" (형식이 다르면 원문 그대로)
function formatDateKo(raw: string) {
  const m = raw.trim().match(/^(\d{4})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*(\d{1,2})/);
  if (!m) return raw.trim();
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`;
}

// 숫자 범위만 입력된 값(예: 6550~6600)에는 단위를 붙임
function withUnit(v: string) {
  const t = v.trim();
  return /^[\d,\s~\-]+$/.test(t) ? `${t}만원` : t;
}

const PriceTicker = () => {
  const [latest, setLatest] = useState<PriceTrendRow | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/price-trends');
        if (!res.ok) return;
        const json = (await res.json()) as { items: PriceTrendRow[] };
        const items = json.items ?? [];
        // PricingSection과 동일하게 마지막 행을 가장 최신 시세로 사용
        setLatest(items.length ? items[items.length - 1] : null);
      } catch {
        // ignore
      }
    };
    void load();
  }, []);

  if (!latest) return null;

  // 표시 날짜는 시세 등록일이 아닌 오늘(한국시간). 값은 가장 최근 등록된 시세를 사용
  const todayLabel = formatDateKo(kstYmd());

  const entries = [
    { label: '주선면허', value: withUnit(latest.broker), color: COLORS.broker },
    { label: '개인화물(소형)', value: withUnit(latest.light), color: COLORS.light },
    { label: '개인화물(중형~16톤)', value: withUnit(latest.medium), color: COLORS.medium },
  ];

  const content = (hidden: boolean) => (
    <div data-component-id="src/components/common/PriceTicker.tsx:65:4" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="65" data-component-end-line="82" data-component-file="PriceTicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20shrink-0%20items-center%20gap-8%20pr-8%20text-xs%20sm%3Atext-sm%22%7D"
      className="flex shrink-0 items-center gap-8 pr-8 text-xs sm:text-sm"
      aria-hidden={hidden || undefined}
    >
      <span data-component-id="src/components/common/PriceTicker.tsx:69:6" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="69" data-component-end-line="69" data-component-file="PriceTicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-text-primary%22%2C%22textContent%22%3A%22%EC%8B%9C%EC%84%B8%22%7D" className="font-semibold text-text-primary">{todayLabel} 시세</span>
      {entries.map((e) => (
        <span data-component-id="src/components/common/PriceTicker.tsx:71:8" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="71" data-component-end-line="80" data-component-file="PriceTicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-1.5%22%7D" key={e.label} className="inline-flex items-center gap-1.5">
          <span data-component-id="src/components/common/PriceTicker.tsx:72:10" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="72" data-component-end-line="75" data-component-file="PriceTicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-block%20h-2%20w-2%20rounded-full%22%7D"
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: e.color }}
          />
          <span data-component-id="src/components/common/PriceTicker.tsx:76:10" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="76" data-component-end-line="76" data-component-file="PriceTicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-text-secondary%22%7D" className="text-text-secondary">{e.label}</span>
          <span data-component-id="src/components/common/PriceTicker.tsx:77:10" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="77" data-component-end-line="79" data-component-file="PriceTicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%7D" className="font-semibold" style={{ color: e.color }}>
            {e.value}
          </span>
        </span>
      ))}
    </div>
  );

  return (
    <div data-component-id="src/components/common/PriceTicker.tsx:86:4" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="86" data-component-end-line="108" data-component-file="PriceTicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22price-ticker%22%2C%22className%22%3A%22fixed%20left-0%20right-0%20top-16%20lg%3Atop-20%20z-nav%20h-9%20border-b%20border-border%20bg-secondary%2F95%20backdrop-blur-nav%22%7D"
      id="price-ticker"
      role="marquee"
      aria-label={`${todayLabel} 최신 시세`}
      className="fixed left-0 right-0 top-16 lg:top-20 z-nav h-9 border-b border-border bg-secondary/95 backdrop-blur-nav"
    >
      {/* 모바일: 우측 "시세/BGM" 버튼 영역을 비워 왼쪽에서만 흐르게 함 */}
      <div data-component-id="src/components/common/PriceTicker.tsx:93:6" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="93" data-component-end-line="107" data-component-file="PriceTicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20h-full%20px-4%20lg%3Apx-6%22%7D" className="container mx-auto h-full px-4 lg:px-6">
        <div data-component-id="src/components/common/PriceTicker.tsx:94:8" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="94" data-component-end-line="106" data-component-file="PriceTicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20h-full%20items-center%22%7D" className="flex h-full items-center">
          <span data-component-id="src/components/common/PriceTicker.tsx:95:10" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="95" data-component-end-line="97" data-component-file="PriceTicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-3%20shrink-0%20rounded-full%20bg-primary%20px-2.5%20py-0.5%20text-%5B11px%5D%20font-bold%20text-primary-foreground%22%2C%22textContent%22%3A%22%EC%B5%9C%EC%8B%A0%20%EC%8B%9C%EC%84%B8%22%7D" className="mr-3 shrink-0 rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-bold text-primary-foreground">
            최신 시세
          </span>
          <div data-component-id="src/components/common/PriceTicker.tsx:98:10" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="98" data-component-end-line="105" data-component-file="PriceTicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22group%20relative%20h-full%20min-w-0%20flex-1%20overflow-hidden%20mr-%5B11.5rem%5D%20sm%3Amr-%5B22rem%5D%20%5Bmask-image%3Alinear-gradient(to_right%2Ctransparent%2C%23000_16px%2C%23000_calc(100%25-24px)%2Ctransparent)%5D%22%7D"
            className="group relative h-full min-w-0 flex-1 overflow-hidden mr-[11.5rem] sm:mr-[22rem] [mask-image:linear-gradient(to_right,transparent,#000_16px,#000_calc(100%-24px),transparent)]"
          >
            <div data-component-id="src/components/common/PriceTicker.tsx:101:12" data-component-path="src/components/common/PriceTicker.tsx" data-component-line="101" data-component-end-line="104" data-component-file="PriceTicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20h-full%20w-max%20items-center%20animate-ticker%20motion-reduce%3Aanimate-none%20group-hover%3A%5Banimation-play-state%3Apaused%5D%22%7D" className="flex h-full w-max items-center animate-ticker motion-reduce:animate-none group-hover:[animation-play-state:paused]">
              {content(false)}
              {content(true)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceTicker;
